import { CIStatusCapsule, CITableGrid } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Labels, SummaryConfigDetailType, SummaryGridProps } from './utility/TypeDefs';
import { capitalizeWords, splitCamelCase } from './utility/Utils';

import classes from './SummaryGrid.module.scss';

const SummaryGrid = ({ gridData, recordType }: SummaryGridProps) => {
  const { t: translate } = useTranslation();

  const [configList, setConfigList] = useState<SummaryConfigDetailType[]>(gridData);
  const [page, setPage] = useState<number>(0);
  const [tableHeaders, setTableHeaders] = useState([{
    name: Labels.CONFIG_NAME,
    label: translate('journey_import_summary.config_name'),
    columnData: {
      field: Labels.CONFIG_NAME
    }
  }]);

  /**
     * @method fieldDetails
     * @description Render Field Details for config name and error message column
     *  @param {string} value - config name or error message
     */
  const fieldDetails = (value: string) => (
    <div className={classes['field-details']} title={value}>
      <p className={classes['field-details-text']}>{value}
      </p>
    </div>
  );

  /**
     * @method renderConfigName
     * @description Render Config name
     * @param {SummaryConfigDetailType} row - config details.
     */
  const renderConfigName = (row: SummaryConfigDetailType) => {
    const value = row[Labels.CONFIG_NAME];
    return fieldDetails(value);
  };

  /**
     * @method renderErrorMessage
     * @description Render Error message
     * @param {SummaryConfigDetailType} row - config details.
     */
  const renderErrorMessage = (row: SummaryConfigDetailType) => {
    const value = row[Labels.ERROR_MESSAGE];
    return fieldDetails(value);
  };

  /**
     * @method renderSummaryStatus
     * @description Render Summary Status
     * @param {SummaryConfigDetailType} row - config details.
     */
  const renderSummaryStatus = (row: SummaryConfigDetailType) => (
    <div>
      <CIStatusCapsule status={row.status} />
    </div>
  );

  /**
     * @method setGridTableHeaders
     * @description Sets grid header configuration based on configType
     * @param {string} configType - config type.
     */
  const setGridTableHeaders = (configType: string) => {
    const configHeaderLabel = configType ? `${capitalizeWords(splitCamelCase(configType))} ${translate('create_journey_component.txt_group_name_placeholder')}` : translate('journey_import_summary.config_name');

    const headers = [{
      name: Labels.CONFIG_NAME,
      label: configHeaderLabel,
      options: { breakPointWidth: 3 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.CONFIG_NAME, render: renderConfigName
      }
    },
    {
      name: Labels.STATUS,
      label: translate('journey_import_summary.status'),
      options: { breakPointWidth: 1 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.STATUS, render: renderSummaryStatus
      }
    },
    {
      name: Labels.ERROR_MESSAGE,
      label: translate('journey_import_summary.error_message'),
      options: { breakPointWidth: 8 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.ERROR_MESSAGE, render: renderErrorMessage
      }
    }];

    setTableHeaders(headers);
  };

  /**
    * @method onPageNoUpdate
    * @description Sets page for pagination
    * @param {number} pageNumber - Page Number.
    */
  const onPageNoUpdate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const footerOptionsObj = {
    selectItems: [5],
    itemsPerPage: 5,
    currentPageNumber: page,
    numberOfPages: Math.ceil((gridData?.length ?? 0) / 5),
    onCurrentPageNumberChange: onPageNoUpdate,
    onItemsPerPageChange: () => { },
    key: page + 1
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  // sets header configuration on mount
  useEffect(() => {
    setGridTableHeaders(recordType);
  }, []);

  // For handling pagination
  useEffect(() => {
    if ((page + 1) > Math.ceil((gridData?.length ?? 0) / 5) && page) {
      onPageNoUpdate(Math.ceil((gridData?.length ?? 0) / 5) - 1);
    }
    const list = gridData?.filter((_exception, index: number) => (page * 5) <= index && index < (page * 5) + 5);
    setConfigList(list);
  }, [page, gridData]);

  return (
    <CITableGrid
      headers={tableHeaders}
      sorted={{ sortedName: '', sortedOrder: '' }}
      footer={footerObj}
      tableData={configList}
    />
  );
};

export default SummaryGrid;
